import { Routes } from '@angular/router';
import { AuthGuard } from '@guards/auth.guard';
import { PendingChangesGuard } from '@guards/pending-changes.guard';
import {
  NO_LONGER_AVAILABLE_PATH,
  redirectSyncWorkerAccountsGuard,
} from '@guards/redirect-sync-worker-accounts.guard';
import { redirectSyncRobotAccountsHomeGuard } from '@guards/redirect-sync-robot-accounts-home.guard';

const modalRoutes: Routes = [
  {
    path: 'confirm',
    loadComponent: () =>
      import('./components/confirm/confirm.component').then(
        (m) => m.ConfirmComponent,
      ),
    outlet: 'modal',
  },
  {
    path: 'match',
    loadComponent: () =>
      import('./components/match/match.component').then(
        (m) => m.MatchComponent,
      ),
    outlet: 'modal',
  },
];

const accountingPages: Routes = [
  {
    path: 'home',
    title: 'Home',
    loadComponent: () =>
      import('./components/home/home.component').then((m) => m.HomeComponent),
  },
  {
    path: 'estimatesync',
    title: 'Publish Estimates',
    loadComponent: () =>
      import('./components/estimate-sync/estimate-sync.component').then(
        (m) => m.EstimateSyncComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'invoicesync',
    title: 'Publish Invoices',
    loadComponent: () =>
      import('./components/invoices-sync/invoices-sync.component').then(
        (m) => m.InvoicesSyncComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'invoicesettings',
    title: 'Publish Invoices',
    loadComponent: () =>
      import('./components/invoices-settings/invoices-settings.component').then(
        (m) => m.InvoicesSettingsComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'paymentsettings',
    title: 'Payment Settings',
    loadComponent: () =>
      import('./components/payments-settings/payments-settings.component').then(
        (m) => m.PaymentsSettingsComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'paymentsync',
    title: 'Publish Payments',
    loadComponent: () =>
      import('./components/payments-sync/payments-sync.component').then(
        (m) => m.PaymentsSyncComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'status',
    title: 'Status',
    loadComponent: () =>
      import('./components/status/status.component').then(
        (m) => m.StatusComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'estimatesettings',
    title: 'Estimating Settings',
    loadComponent: () =>
      import('./components/estimate-settings/estimate-settings.component').then(
        (m) => m.EstimateSettingsComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'setupimport',
    title: 'Import Customers',
    loadComponent: () =>
      import('./components/setup-import/setup-import.component').then(
        (m) => m.SetupImportComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'setupimportvendors',
    title: 'Import Vendors',
    loadComponent: () =>
      import(
        './components/setup-import-vendor/setup-import-vendor.component'
      ).then((m) => m.SetupImportVendorComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'setupclasses',
    title: 'Match Classes',
    loadComponent: () =>
      import(
        './components/setup-cost-code-classes/setup-cost-code-classes.component'
      ).then((m) => m.SetupCostCodeClassesComponent),
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'setupjobs',
    title: 'Match Customers',
    loadComponent: () =>
      import('./components/setup-customers/setup-customers.component').then(
        (m) => m.SetupCustomersComponent,
      ),
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'setupcostcodes',
    title: 'Match Cost Codes',
    loadComponent: () =>
      import('./components/setup-cost-codes/setup-cost-codes.component').then(
        (m) => m.SetupCostCodesComponent,
      ),
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'setuppaymethods',
    title: 'Match Payment Methods',
    loadComponent: () =>
      import('./components/payment-methods/payment-methods.component').then(
        (m) => m.PaymentMethodsComponent,
      ),
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'setupvendors',
    title: 'Match Vendors',
    loadComponent: () =>
      import('./components/setup-vendors/setup-vendors.component').then(
        (m) => m.SetupVendorsComponent,
      ),
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'setupstaff',
    title: 'Match Staff',
    loadComponent: () =>
      import('./components/setup-staff/setup-staff.component').then(
        (m) => m.SetupStaffComponent,
      ),
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'setuptaxes',
    title: 'Match Taxes',
    loadComponent: () =>
      import('./components/setup-tax-groups/setup-tax-groups.component').then(
        (m) => m.SetupTaxGroupsComponent,
      ),
    canActivate: [AuthGuard],
    canDeactivate: [PendingChangesGuard],
  },
  {
    path: 'vendorbillsettings',
    title: 'Vendor Bills Settings',
    loadComponent: () =>
      import(
        './components/vendorbill-settings/vendorbill-settings.component'
      ).then((m) => m.VendorbillSettingsComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'vendorbillsync',
    title: 'Publish Vendor Bills',
    loadComponent: () =>
      import('./components/vendorbill-sync/vendorbill-sync.component').then(
        (m) => m.VendorbillSyncComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'timesync',
    title: 'Publish Time to Quickbooks',
    loadComponent: () =>
      import(
        './components/publish-timesheets/publish-timesheets.component'
      ).then((m) => m.PublishTimesheetsComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'timesettings',
    title: 'Time Settings',
    loadComponent: () =>
      import('./components/time-settings/time-settings.component').then(
        (m) => m.TimeSettingsComponent,
      ),
    canActivate: [AuthGuard],
  },
];

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./components/login/login.component').then(
        (m) => m.LoginComponent,
      ),
  },
  {
    path: NO_LONGER_AVAILABLE_PATH,
    title: 'No Longer Available',
    loadComponent: () =>
      import(
        './components/no-longer-available/no-longer-available.component'
      ).then((m) => m.NoLongerAvailableComponent),
    canActivate: [redirectSyncRobotAccountsHomeGuard],
  },
  {
    path: '',
    canActivateChild: [redirectSyncWorkerAccountsGuard],
    children: accountingPages,
  },
  {
    path: '**',
    redirectTo: 'home',
  },
  ...modalRoutes,
];
